import {isUrl} from '@packages/utilities/string-utils';

export const validateUrl = (url: string): string | null => {
  if (!url || url === '') {
    return 'URL required';
  }
  try {
    const newUrl = new URL(url);
    if (newUrl.protocol === 'https:') {
      return null;
    }
    return 'Invalid URL format';
  } catch {
    return 'Invalid URL format';
  }
};

export const isValidCanonicalUrl = (url: string): boolean => {
  // Check if the URL starts with http:// or https://
  if (!/^https?:\/\//.test(url)) {
    return false;
  }

  if (!isUrl(url)) {
    return false;
  }

  // Check if the URL ends with a trailing slash
  if (/\/$/.test(url)) {
    return false;
  }

  return true;
};

/**
 * Normalizes a URL for use as a canonical URL by:
 * 1. Adding https:// if no protocol is present
 * 2. Converting http:// to https://
 * 3. Removing trailing slashes
 * 4. Validating URL format
 *
 * @param url The URL to normalize for canonical use
 * @returns The normalized canonical URL, or null if the URL is invalid
 * @throws Error if the URL cannot be normalized
 */
export const normalizeCanonicalUrl = (url: string): string | null => {
  if (!url) return null;

  // Trim whitespace and convert to lowercase for protocol check
  const trimmedUrl = url.trim();
  if (!trimmedUrl) return null;

  try {
    // Add https:// if no protocol present
    const urlWithProtocol = /^https?:\/\//i.test(trimmedUrl)
      ? trimmedUrl
      : `https://${trimmedUrl}`;

    // Validate URL format
    if (!isUrl(urlWithProtocol)) {
      return null;
    }

    const urlObj = new URL(urlWithProtocol);

    // Force https protocol
    urlObj.protocol = 'https:';

    // Remove all trailing slashes after the domain name
    let normalizedUrl = urlObj.toString();
    normalizedUrl = normalizedUrl.replace(/\/+$/, '');

    return normalizedUrl;
  } catch (e) {
    return null;
  }
};
